import { createFileRoute } from '@tanstack/react-router'
import React, { useState, useMemo, useRef, useEffect } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import Fuse, { type FuseResultMatch } from 'fuse.js'
import { useAction } from 'convex/react'
import { api } from 'convex/_generated/api'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import type { Id } from 'convex/_generated/dataModel'
import {
  MEXICAN_MUSIC_STYLES,
  getAllSearchableItems,
  type MusicStyle,
} from '@/consts/es-music-consts'

// Import all song JSON files
import songs1 from '@/consts/deepseek_json_20251112_019866.json'
import songs2 from '@/consts/deepseek_json_20251112_580319.json'
import songs3 from '@/consts/deepseek_json_20251112_60d53b.json'
import songs4 from '@/consts/deepseek_json_20251112_70ba76.json'
import songs5 from '@/consts/deepseek_json_20251112_70c7b4.json'
import songs6 from '@/consts/deepseek_json_20251112_84cf54.json'
import songs7 from '@/consts/deepseek_json_20251112_8d57c9.json'
import songs8 from '@/consts/deepseek_json_20251112_8dd31c.json'
import songs9 from '@/consts/deepseek_json_20251112_976eac.json'
import songs10 from '@/consts/deepseek_json_20251112_9e35a1.json'
import songs11 from '@/consts/deepseek_json_20251112_c55c15.json'
import songs12 from '@/consts/deepseek_json_20251112_e7bfa7.json'
import songs13 from '@/consts/deepseek_json_20251112_fa3f12.json'

export const Route = createFileRoute('/app/es-basic/create')({
  component: EsBasicCreatePage,
})

interface Song {
  title: string
  sections: Array<{
    type: string
    spanish: string
    english: string
  }>
}

// Combine all songs from different files
function getAllSongs(): Song[] {
  const allSongs: Song[] = []
  
  // Helper to safely add songs from a file
  const addSongs = (data: unknown) => {
    if (Array.isArray(data)) {
      allSongs.push(...data.filter((item): item is Song => 
        item && typeof item === 'object' && 'title' in item && 'sections' in item
      ))
    } else if (data && typeof data === 'object' && 'adjectives' in data) {
      // Skip adjective list files
      return
    }
  }

  addSongs(songs1)
  addSongs(songs2)
  addSongs(songs3)
  addSongs(songs4)
  addSongs(songs5)
  addSongs(songs6)
  addSongs(songs7)
  addSongs(songs8)
  addSongs(songs9)
  addSongs(songs10)
  addSongs(songs11)
  addSongs(songs12)
  addSongs(songs13)

  return allSongs
}

// Convert song sections to lyrics format
function songToLyrics(song: Song): string {
  return song.sections
    .map(section => {
      const label = section.type.toUpperCase()
      return `[${label}]\n${section.spanish}`
    })
    .join('\n\n')
}

function EsBasicCreatePage() {
  const currentUser = useCurrentUser()

  if (currentUser === undefined) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-sky-blue text-xl font-mono">Loading...</div>
      </div>
    )
  }

  if (currentUser === null) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-trail-tan text-xl font-mono">Please sign in to create songs</div>
      </div>
    )
  }

  return <EsBasicCreateContent userId={currentUser._id} />
}

function EsBasicCreateContent({ userId }: { userId: Id<'users'> }) {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchLanguage, setSearchLanguage] = useState<'spanish' | 'english'>('spanish')
  const [selectedSong, setSelectedSong] = useState<Song | null>(null)
  const [prompt, setPrompt] = useState('')
  const [musicStyleQuery, setMusicStyleQuery] = useState('')
  const [selectedMusicStyle, setSelectedMusicStyle] = useState<MusicStyle | null>(null)
  const [showMusicStyleSearch, setShowMusicStyleSearch] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [validationError, setValidationError] = useState<string | null>(null)

  const generateSong = useAction(api.tempolor.generateSong)
  const parentRef = useRef<HTMLDivElement>(null)
  const musicStyleParentRef = useRef<HTMLDivElement>(null)

  // Load all songs
  const allSongs = useMemo(() => getAllSongs(), [])

  // Create searchable text for each song
  const songsWithSearchText = useMemo(() => {
    return allSongs.map(song => ({
      song,
      spanishText: [
        song.title,
        ...song.sections.map(s => s.spanish),
      ].join(' '),
      englishText: [
        song.title,
        ...song.sections.map(s => s.english),
      ].join(' '),
    }))
  }, [allSongs])

  // Setup Fuse.js for fuzzy search - search both Spanish and English
  const fuse = useMemo(() => {
    return new Fuse(songsWithSearchText, {
      keys: [
        { name: 'spanishText', weight: searchLanguage === 'spanish' ? 1 : 0.5 },
        { name: 'englishText', weight: searchLanguage === 'english' ? 1 : 0.5 },
      ],
      threshold: 0.3, // Lower = more strict, higher = more fuzzy
      includeScore: true,
      includeMatches: true, // Include match information for highlighting
      minMatchCharLength: 1,
    })
  }, [songsWithSearchText, searchLanguage])

  // Filter songs based on search query
  const filteredSongs = useMemo(() => {
    if (!searchQuery.trim()) {
      return songsWithSearchText.map(item => ({ 
        item: item.song, 
        score: 0,
        matches: null,
        spanishText: item.spanishText,
        englishText: item.englishText,
      }))
    }

    const results = fuse.search(searchQuery)
    return results.map(result => ({
      item: result.item.song,
      score: result.score || 0,
      matches: result.matches || [],
      spanishText: result.item.spanishText,
      englishText: result.item.englishText,
    }))
  }, [searchQuery, fuse, songsWithSearchText])

  // Pagination: show max 10 items at a time
  const itemsPerPage = 10
  const [currentPage, setCurrentPage] = useState(0)
  const totalPages = Math.ceil(filteredSongs.length / itemsPerPage)
  const startIndex = currentPage * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedSongs = filteredSongs.slice(startIndex, endIndex)

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(0)
  }, [searchQuery, searchLanguage])

  // Close music style dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        musicStyleParentRef.current &&
        !musicStyleParentRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('input[type="text"]')
      ) {
        setShowMusicStyleSearch(false)
      }
    }

    if (showMusicStyleSearch) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [showMusicStyleSearch])

  // Music style search setup
  const musicStyleItems = useMemo(() => getAllSearchableItems(), [])
  const musicStyleFuse = useMemo(() => {
    return new Fuse(musicStyleItems, {
      keys: ['name', 'description', 'keywords'],
      threshold: 0.3,
      includeScore: true,
      includeMatches: true,
      minMatchCharLength: 1,
    })
  }, [musicStyleItems])

  const filteredMusicStyles = useMemo(() => {
    if (!musicStyleQuery.trim()) {
      return MEXICAN_MUSIC_STYLES.slice(0, 10).map(style => ({
        item: style,
        score: 0,
      }))
    }

    const results = musicStyleFuse.search(musicStyleQuery)
    // Filter to only return music styles (not instruments or vocals)
    return results
      .filter(result => result.item.type === 'style')
      .map(result => ({
        item: MEXICAN_MUSIC_STYLES.find(s => s.name === result.item.name)!,
        score: result.score || 0,
      }))
      .filter(item => item.item !== undefined)
  }, [musicStyleQuery, musicStyleFuse])

  const musicStyleVirtualizer = useVirtualizer({
    count: Math.min(filteredMusicStyles.length, 10),
    getScrollElement: () => musicStyleParentRef.current,
    estimateSize: () => 100,
    overscan: 2,
  })

  // Virtualizer for the list
  const virtualizer = useVirtualizer({
    count: paginatedSongs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 180, // Increased height to accommodate match snippets
    overscan: 2,
  })

  // Helper function to extract matching lines from song sections
  const getMatchingLines = (
    match: FuseResultMatch | undefined,
    song: Song,
    language: 'spanish' | 'english'
  ): Array<{ section: string; text: string; matchedText: string }> => {
    if (!match || !match.indices || match.indices.length === 0) return []

    const matchingLines: Array<{ section: string; text: string; matchedText: string }> = []

    // Check each section for matches
    for (const section of song.sections) {
      const sectionText = language === 'spanish' ? section.spanish : section.english
      const fullTextBeforeSection = language === 'spanish'
        ? [song.title, ...song.sections.slice(0, song.sections.indexOf(section)).map(s => s.spanish)].join(' ')
        : [song.title, ...song.sections.slice(0, song.sections.indexOf(section)).map(s => s.english)].join(' ')
      
      const sectionStartIndex = fullTextBeforeSection.length + (fullTextBeforeSection.length > 0 ? 1 : 0)
      const sectionEndIndex = sectionStartIndex + sectionText.length

      // Check if any match indices fall within this section
      for (const [start, end] of match.indices) {
        if (start >= sectionStartIndex && end <= sectionEndIndex) {
          // Extract the matched portion from the section text
          const matchStartInSection = start - sectionStartIndex
          const matchEndInSection = end - sectionStartIndex + 1
          const matchedText = sectionText.substring(matchStartInSection, matchEndInSection)
          
          matchingLines.push({
            section: section.type,
            text: sectionText,
            matchedText,
          })
          break // Only add each section once
        }
      }
    }

    // Also check title
    if (match.indices.some(([start]: [number, number]) => start < song.title.length)) {
      const titleMatch = match.indices.find(([start]: [number, number]) => start < song.title.length)
      if (titleMatch) {
        const [start, end] = titleMatch
        matchingLines.unshift({
          section: 'title',
          text: song.title,
          matchedText: song.title.substring(start, end + 1),
        })
      }
    }

    return matchingLines
  }

  const handleSelectSong = (song: Song) => {
    setSelectedSong(song)
    // Auto-fill prompt with song title
    setPrompt(`Spanish learning song: ${song.title}`)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setValidationError(null)

    if (!selectedSong) {
      setValidationError('Please select a song from the list')
      return
    }

    if (!prompt.trim()) {
      setValidationError('Please enter a music theme/prompt')
      return
    }

    setIsGenerating(true)
    try {
      const lyrics = songToLyrics(selectedSong)
      
      // Build enhanced prompt with music style information
      let enhancedPrompt = prompt.trim()
      if (selectedMusicStyle) {
        enhancedPrompt = `${enhancedPrompt}\n\nMusic style: ${selectedMusicStyle.name}\nDescription: ${selectedMusicStyle.description}\nInstruments: ${selectedMusicStyle.instruments.join(', ')}${selectedMusicStyle.vocalStyle ? `\nVocal style: ${selectedMusicStyle.vocalStyle}` : ''}${selectedMusicStyle.tempo ? `\nTempo: ${selectedMusicStyle.tempo}` : ''}`
      }

      await generateSong({
        userId,
        prompt: enhancedPrompt,
        model: 'TemPolor v3.5',
        lyrics,
        title: selectedSong.title.split(' / ')[1] || selectedSong.title,
      })

      // Reset form
      setSelectedSong(null)
      setPrompt('')
      setValidationError(null)
    } catch (error) {
      console.error('Failed to generate:', error)
      setValidationError(error instanceof Error ? error.message : 'Generation failed')
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <div className="min-h-screen bg-black text-white py-4 sm:py-8 px-2 sm:px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4 sm:mb-8 terminal-border p-4 sm:p-6">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-sky-blue mb-2 font-mono tracking-wider">
            [ES-BASIC: CREATE SONG]
          </h1>
          <p className="text-trail-tan font-mono mb-2 text-sm sm:text-base">
            Select a premade Spanish learning song and create it with AI music
          </p>
          <div className="text-trail-tan text-xs font-mono mt-2">
            Total songs available: {allSongs.length}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* LEFT: Song Selection */}
          <div className="order-2 lg:order-1">
            <div className="terminal-border p-4 sm:p-6 bg-black mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-terminal-green mb-3 sm:mb-4 font-mono">
                [SELECT SONG]
              </h2>

              {/* Search Input */}
              <div className="mb-4">
                <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                  ► SEARCH SONGS
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={`Search in ${searchLanguage === 'spanish' ? 'Spanish' : 'English'}...`}
                    className="flex-1 px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                  />
                </div>

                {/* Language Toggle */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setSearchLanguage('spanish')}
                    className={`px-4 py-2 font-bold font-mono text-sm border-2 transition-all ${
                      searchLanguage === 'spanish'
                        ? 'bg-sky-blue text-black border-sky-blue'
                        : 'bg-black text-sky-blue border-sky-blue hover:bg-sky-blue hover:text-black'
                    }`}
                  >
                    SPANISH
                  </button>
                  <button
                    type="button"
                    onClick={() => setSearchLanguage('english')}
                    className={`px-4 py-2 font-bold font-mono text-sm border-2 transition-all ${
                      searchLanguage === 'english'
                        ? 'bg-sky-blue text-black border-sky-blue'
                        : 'bg-black text-sky-blue border-sky-blue hover:bg-sky-blue hover:text-black'
                    }`}
                  >
                    ENGLISH
                  </button>
                </div>

                <div className="text-trail-tan text-xs font-mono mt-2">
                  Found {filteredSongs.length} song{filteredSongs.length !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Virtualized Song List */}
              <div
                ref={parentRef}
                className="border-2 border-sky-blue bg-black"
                style={{ height: '400px', overflow: 'auto' }}
              >
                {paginatedSongs.length === 0 ? (
                  <div className="p-8 text-center text-trail-tan font-mono">
                    No songs found. Try a different search term.
                  </div>
                ) : (
                  <div
                    style={{
                      height: `${virtualizer.getTotalSize()}px`,
                      width: '100%',
                      position: 'relative',
                    }}
                  >
                    {virtualizer.getVirtualItems().map((virtualItem) => {
                      const songData = paginatedSongs[virtualItem.index]
                      const song = songData.item
                      const isSelected = selectedSong?.title === song.title
                      const matches = songData.matches || []

                      // Find matching sections for Spanish and English
                      const spanishMatch = matches.find(m => m.key === 'spanishText')
                      const englishMatch = matches.find(m => m.key === 'englishText')

                      // Get matching lines for both languages
                      const spanishMatches = getMatchingLines(spanishMatch, song, 'spanish')
                      const englishMatches = getMatchingLines(englishMatch, song, 'english')

                      return (
                        <div
                          key={virtualItem.key}
                          data-index={virtualItem.index}
                          ref={virtualizer.measureElement}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            transform: `translateY(${virtualItem.start}px)`,
                          }}
                        >
                          <div
                            onClick={() => handleSelectSong(song)}
                            className={`p-4 m-2 cursor-pointer transition-all border-2 ${
                              isSelected
                                ? 'bg-terminal-green border-terminal-green text-black'
                                : 'bg-black border-sky-blue text-white hover:border-terminal-green'
                            }`}
                          >
                            <div className="font-mono">
                              <div className={`font-bold text-sm mb-2 ${isSelected ? 'text-black' : 'text-sky-blue'}`}>
                                {song.title}
                              </div>
                              <div className={`text-xs ${isSelected ? 'text-black' : 'text-trail-tan'} mb-2`}>
                                {song.sections.length} section{song.sections.length !== 1 ? 's' : ''}
                              </div>
                              
                              {/* Show matching lines if search query exists */}
                              {searchQuery.trim() && (spanishMatches.length > 0 || englishMatches.length > 0) && (
                                <div className={`mt-2 space-y-2 text-xs ${isSelected ? 'text-black' : 'text-trail-tan'}`}>
                                  {spanishMatches.length > 0 && (
                                    <div>
                                      <div className={`font-bold mb-1 ${isSelected ? 'text-black' : 'text-sky-blue'}`}>
                                        ES ({spanishMatches.length}):
                                      </div>
                                      {spanishMatches.map((match, idx) => (
                                        <div key={idx} className="ml-2 mb-1">
                                          <span className={`font-semibold ${isSelected ? 'text-black' : 'text-sky-blue'}`}>
                                            [{match.section.toUpperCase()}]:
                                          </span>{' '}
                                          <span dangerouslySetInnerHTML={{
                                            __html: match.text.replace(
                                              new RegExp(match.matchedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
                                              (matched) => `<mark class="${isSelected ? 'bg-yellow-300 text-black' : 'bg-yellow-600 text-white'}">${matched}</mark>`
                                            )
                                          }} />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  {englishMatches.length > 0 && (
                                    <div>
                                      <div className={`font-bold mb-1 ${isSelected ? 'text-black' : 'text-sky-blue'}`}>
                                        EN ({englishMatches.length}):
                                      </div>
                                      {englishMatches.map((match, idx) => (
                                        <div key={idx} className="ml-2 mb-1">
                                          <span className={`font-semibold ${isSelected ? 'text-black' : 'text-sky-blue'}`}>
                                            [{match.section.toUpperCase()}]:
                                          </span>{' '}
                                          <span dangerouslySetInnerHTML={{
                                            __html: match.text.replace(
                                              new RegExp(match.matchedText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'),
                                              (matched) => `<mark class="${isSelected ? 'bg-yellow-300 text-black' : 'bg-yellow-600 text-white'}">${matched}</mark>`
                                            )
                                          }} />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              {isSelected && (
                                <div className="text-xs mt-2 text-black font-bold">
                                  ✓ SELECTED
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                    disabled={currentPage === 0}
                    className="px-4 py-2 bg-black text-sky-blue font-bold font-mono text-sm border-2 border-sky-blue hover:bg-sky-blue hover:text-black disabled:bg-gray-900 disabled:border-gray-700 disabled:text-gray-600 transition-all"
                  >
                    ← PREV
                  </button>
                  <div className="text-trail-tan font-mono text-sm">
                    Page {currentPage + 1} of {totalPages}
                  </div>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
                    disabled={currentPage >= totalPages - 1}
                    className="px-4 py-2 bg-black text-sky-blue font-bold font-mono text-sm border-2 border-sky-blue hover:bg-sky-blue hover:text-black disabled:bg-gray-900 disabled:border-gray-700 disabled:text-gray-600 transition-all"
                  >
                    NEXT →
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* RIGHT: Song Creation Form */}
          <div className="order-1 lg:order-2 lg:sticky lg:top-8 lg:self-start">
            <div className="terminal-border p-4 sm:p-6 bg-black">
              <h2 className="text-xl sm:text-2xl font-bold text-terminal-green mb-3 sm:mb-4 font-mono">
                [CREATE SONG]
              </h2>

              {validationError && (
                <div className="bg-red-900 border-2 border-red-500 rounded-lg p-4 mb-6">
                  <p className="text-red-200 font-mono text-sm">{validationError}</p>
                </div>
              )}

              {selectedSong && (
                <div className="mb-6 bg-black border-2 border-terminal-green p-4">
                  <div className="text-terminal-green font-mono font-bold text-sm mb-2">
                    SELECTED SONG:
                  </div>
                  <div className="text-white font-mono text-sm mb-4">
                    {selectedSong.title}
                  </div>
                  <div className="text-trail-tan font-mono text-xs max-h-48 overflow-y-auto">
                    {selectedSong.sections.map((section, idx) => (
                      <div key={idx} className="mb-2">
                        <div className="text-sky-blue font-bold">[{section.type.toUpperCase()}]</div>
                        <div className="text-white">{section.spanish}</div>
                        <div className="text-gray-400 italic">{section.english}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* Music Style Selection */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► MUSIC STYLE <span className="text-trail-tan font-normal">(optional)</span>
                  </label>
                  {selectedMusicStyle ? (
                    <div className="mb-2 bg-black border-2 border-terminal-green p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="text-terminal-green font-mono font-bold text-sm mb-1">
                            {selectedMusicStyle.name}
                          </div>
                          <div className="text-trail-tan font-mono text-xs mb-2">
                            {selectedMusicStyle.description}
                          </div>
                          <div className="text-sky-blue font-mono text-xs">
                            Instruments: {selectedMusicStyle.instruments.join(', ')}
                          </div>
                          {selectedMusicStyle.vocalStyle && (
                            <div className="text-sky-blue font-mono text-xs mt-1">
                              Vocal: {selectedMusicStyle.vocalStyle}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedMusicStyle(null)
                            setMusicStyleQuery('')
                          }}
                          className="ml-2 px-2 py-1 bg-red-600 text-white font-mono text-xs hover:bg-red-700 transition-all"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="relative">
                      <input
                        type="text"
                        value={musicStyleQuery}
                        onChange={(e) => {
                          setMusicStyleQuery(e.target.value)
                          setShowMusicStyleSearch(true)
                        }}
                        onFocus={() => setShowMusicStyleSearch(true)}
                        placeholder="Search Mexican music styles (mariachi, norteño, banda...)"
                        className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none retro-glow transition-all"
                      />
                      {showMusicStyleSearch && filteredMusicStyles.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-black border-2 border-sky-blue max-h-64 overflow-hidden">
                          <div ref={musicStyleParentRef} style={{ height: '256px', overflow: 'auto' }}>
                            <div
                              style={{
                                height: `${musicStyleVirtualizer.getTotalSize()}px`,
                                width: '100%',
                                position: 'relative',
                              }}
                            >
                              {musicStyleVirtualizer.getVirtualItems().map((virtualItem) => {
                                const style = filteredMusicStyles[virtualItem.index].item
                                return (
                                  <div
                                    key={virtualItem.key}
                                    ref={musicStyleVirtualizer.measureElement}
                                    data-index={virtualItem.index}
                                    style={{
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      transform: `translateY(${virtualItem.start}px)`,
                                    }}
                                  >
                                    <div
                                      onClick={() => {
                                        setSelectedMusicStyle(style)
                                        setShowMusicStyleSearch(false)
                                        setMusicStyleQuery('')
                                      }}
                                      className="p-3 m-1 cursor-pointer bg-black border-2 border-sky-blue hover:border-terminal-green transition-all"
                                    >
                                      <div className="text-sky-blue font-mono font-bold text-sm mb-1">
                                        {style.name}
                                      </div>
                                      <div className="text-trail-tan font-mono text-xs line-clamp-2">
                                        {style.description}
                                      </div>
                                    </div>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Music Theme/Style Prompt */}
                <div>
                  <label className="block text-sky-blue font-mono font-bold mb-2 text-sm tracking-wider">
                    ► MUSIC THEME / STYLE DETAILS
                  </label>
                  <textarea
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Describe the music style you want...
Example: 'upbeat pop with electronic synths'
Example: 'melancholic acoustic guitar ballad'
Example: 'energetic rock with electric guitars'"
                    className="w-full px-4 py-3 bg-black border-2 border-sky-blue text-white font-mono text-sm focus:border-terminal-green focus:outline-none resize-none retro-glow transition-all"
                    rows={4}
                    required
                  />
                </div>

                {/* Action Button */}
                <button
                  type="submit"
                  disabled={isGenerating || !selectedSong || !prompt.trim()}
                  className="w-full px-4 sm:px-8 py-3 sm:py-4 bg-terminal-green text-black font-bold font-mono text-base sm:text-lg border-4 border-terminal-green hover:bg-black hover:text-terminal-green disabled:bg-gray-700 disabled:border-gray-700 disabled:text-gray-500 transition-all retro-glow tracking-wider"
                >
                  {isGenerating ? '⏳ GENERATING...' : '► CREATE SONG ◄'}
                </button>
              </form>

              {/* Info Box */}
              <div className="mt-4 sm:mt-6 bg-black border-2 border-trail-tan p-3 sm:p-4">
                <p className="text-trail-tan font-mono text-xs mb-2">
                  <span className="text-sky-blue">►</span> What you&apos;ll get:
                </p>
                <ul className="space-y-1 text-trail-tan font-mono text-xs">
                  <li><span className="text-terminal-green">•</span> AI-generated song with vocals</li>
                  <li><span className="text-terminal-green">•</span> Spanish lyrics from selected song</li>
                  <li><span className="text-terminal-green">•</span> Music matching your theme/style</li>
                  <li><span className="text-terminal-green">•</span> Ready for vocabulary extraction</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

